<template>
    <div class="contact-phone-number-selectable flex flex-col gap-3">
        <vx-selectable
            :ref="selectableRef"
            v-model="input"
            :noTags="true"
            @search="search"
            @search:paste="paste"
            @search:blur="blur"
            placeholder="Select contacts"
            :options="searchParam ? options : []"
            :createOption="createOption"
            @option:selected="selected"
            :closeOnSelect="false"
            :closeOnSearchPaste="true"
            :clearable="false"
            :dropdownShouldOpen="({ open, search }) => open && search"
            :clearSearchOnSelect="true"
            :disabled="props.disabled"
            :clearSearchOnBlur="() => true"
            :mapKeydown="mapKeyDown"
            size="lg"
            :relativeDropdown="true"
        >
            <template v-slot:option="{ option }">
                <contact-phone-number-selectable-row
                    :contact="option"
                    :selectedIndicator="true"
                    :selected="isSelected(option)"
                >
                </contact-phone-number-selectable-row>
            </template>
            <template v-slot:no-options> Paste or press enter to add phone numbers separated by comma. </template>
        </vx-selectable>

        <contact-phone-number-selectable-list
            v-model="contacts"
            :disabled="props.disabled"
            data-test="contacts-selected"
        ></contact-phone-number-selectable-list>

        <contact-phone-number-selectable-list
            v-model="invalidContacts"
            :disabled="props.disabled"
            :invalid="true"
            data-test="contacts-invalid"
        ></contact-phone-number-selectable-list>
    </div>
</template>

<script setup>
import { VxSelectable } from '@voxie/frontend-components';
import { nextTick, ref } from 'vue';
import debounce from '~/utils/debounce';
import ContactPhoneNumberSelectableRow from '~/components/message-hub/message-hub-new-conversation/ContactPhoneNumberSelectableRow.vue';
import { actionsClient, contactsClient, groupContactsClient } from '~/services';
import { isLargeTeamSearchRestricted } from '~/utils/team';
import { e164Format, isValidPhoneNumber } from '~/utils/phoneNumber';
import { formatPhoneNumber } from '~/components/filters';
import { uniqBy } from '~/utils/helpers';
import ContactPhoneNumberSelectableList from '~/components/message-hub/message-hub-new-conversation/ContactPhoneNumberSelectableList.vue';

const teamId = Spark.state.currentTeam.id;

const SEPARATOR_REGEX = /\r?\n|\r|\n|,/g;

const props = defineProps({
    disabled: {
        type: Boolean,
        default: false,
    },
    size: {
        type: String,
        default: 'md',
    },
    groupId: {
        type: String,
        default: undefined,
    },
});

const contacts = defineModel({
    type: Array,
    required: true,
    default: () => [],
});

const invalidContacts = ref([]);

const selectableRef = ref();

const input = ref();
const searchParam = ref('');
const options = ref([]);
const recentlySelected = ref();

const isSelected = (selectedItem) => {
    return Boolean(contacts.value.find((item) => item.id == selectedItem.id));
};

const isPhoneNumberValid = (phoneNumber) => {
    if (phoneNumber.length < 10) {
        return false;
    }

    phoneNumber = e164Format(phoneNumber);

    if (!phoneNumber?.trim()?.length) {
        return false;
    }

    return isValidPhoneNumber(phoneNumber);
};

const mapKeyDown = (event, vueSelect) => {
    const enterHandler = event[13];
    // 13 is the keycode for Enter
    event[13] = (keyEvent) => {
        enterHandler(keyEvent, vueSelect);

        if (!options.value.length) {
            addContactsFromText(vueSelect.search);
            vueSelect.search = '';
        }
    };

    // 188 is the keycode for comma ,
    event[188] = (keyEvent) => {
        keyEvent.preventDefault();

        addContactsFromText(vueSelect.search);
        vueSelect.search = '';
    };

    return event;
};

const createOption = (phoneNumber) => {
    const isValid = isPhoneNumberValid(phoneNumber);
    return {
        phone: isValid ? e164Format(phoneNumber) : phoneNumber,
        label: formatPhoneNumber(phoneNumber),
        invalid: !isValid ? `Not a valid phone number.` : null,
        processing: isValid,
    };
};

const selected = async (event) => {
    if (!contacts.value.some((contact) => contact.phone === event.phone)) {
        if (SEPARATOR_REGEX.test(event.phone)) {
            addContactsFromText(event.phone);
        } else {
            if (event.invalid) {
                invalidContacts.value.unshift(event);
            } else {
                contacts.value.unshift(event);
                processContacts();
            }
        }
    } else {
        remove(event);
    }

    recentlySelected.value = event;

    await nextTick();
    input.value = null;
};

const remove = async (removeItem) => {
    if (props.disabled) {
        return;
    }

    contacts.value = contacts.value.filter((item) => item.phone != removeItem.phone);
};

const search = debounce(async (search, loading) => {
    searchParam.value = search;
    if (!search) {
        options.value = [];
        loading?.(false);
        return;
    }

    loading?.(true);

    const searchKey = isLargeTeamSearchRestricted(teamId, props.groupId)
        ? search.includes('@')
            ? 'filter[email]'
            : 'filter[phone]'
        : 'filter[search]';

    const params = {
        per_page: 10,
        [searchKey]: search,
    };

    try {
        const response = props.groupId
            ? await groupContactsClient.groupContactsList(teamId, props.groupId, params)
            : await contactsClient.searchContacts(teamId, params);

        options.value = response.data.data.map((contact) => ({
            ...contact,
            label: `${contact.first_name} ${contact.last_name} ${formatPhoneNumber(contact.phone)}`,
        }));
    } finally {
        loading?.(false);
    }
}, 300);

const mapTextIntoContacts = (text) => {
    return uniqBy(
        text
            .split(SEPARATOR_REGEX)
            .filter((phoneNumber) => phoneNumber?.trim?.().length)
            .map(createOption),
        'phone'
    );
};

const addContactsFromText = async (text) => {
    const newContacts = mapTextIntoContacts(text)
        // only is new if non existing in the selected contacts
        .filter((newContact) => !contacts.value.find((contact) => contact.phone === newContact.phone));

    if (!newContacts.length) {
        return;
    }

    const invalidList = newContacts.filter((contact) => contact.invalid).reverse();
    const validList = newContacts.filter((contact) => !contact.invalid).reverse();

    contacts.value = [...validList, ...contacts.value];
    invalidContacts.value = [...invalidList, ...invalidContacts.value];

    processContacts();
};

const paste = (event) => {
    const text = event.clipboardData?.getData('text')?.trim();

    if (!text) {
        return;
    }

    if (!SEPARATOR_REGEX.test(text)) {
        return;
    }

    event.preventDefault();

    addContactsFromText(text);
};

const blur = () => {
    if (isValidPhoneNumber(searchParam.value)) {
        addContactsFromText(searchParam.value);
    }
};

const processContacts = async () => {
    await nextTick();

    contacts.value.forEach((contact) => {
        if (!contact.processing || contact.id) {
            return;
        }

        lookupContact(contact.phone)
            .then((lookupContact) => {
                const index = contacts.value.findIndex((existingContact) => existingContact.phone === contact.phone);

                if (index === -1) {
                    return;
                }

                contacts.value[index].id = lookupContact.id;
                contacts.value[index].first_name = lookupContact.first_name;
                contacts.value[index].last_name = lookupContact.last_name;

                contacts.value[index].processing = false;
            })
            .catch((error) => {
                const index = contacts.value.findIndex((existingContact) => existingContact.phone === contact.phone);

                if (index === -1) {
                    return;
                }

                contacts.value[index].id = null;

                contacts.value[index].invalid =
                    error?.message ||
                    `It was not possible to process the phone number ${formatPhoneNumber(contact.phone)}.`;
                contacts.value[index].processing = false;
            });
    });
};

const lookupContact = async (phoneNumber) => {
    try {
        return await fetchContact(phoneNumber);
    } catch {
        return await createContact(phoneNumber);
    }
};

const fetchContact = async (phoneNumber) => {
    try {
        const payload = {
            'filter[phone]': phoneNumber,
            per_page: 1,
        };

        const response = props.groupId
            ? await groupContactsClient.groupContactsList(teamId, props.groupId, payload)
            : await contactsClient.searchContacts(teamId, payload);

        if (response.data?.data?.length === 1 && response.data.data[0].phone === phoneNumber) {
            return response.data.data[0];
        }
    } catch (e) {
        console.error(e);
    }

    throw new Error(`It was not possible to fetch a contact with the phone number ${phoneNumber}.`);
};

const createContact = async (phoneNumber) => {
    try {
        const payload = { phone: phoneNumber };
        const response = props.groupId
            ? await groupContactsClient.groupContactSave(teamId, props.groupId, payload)
            : await contactsClient.contactSave(teamId, payload);

        actionsClient.send('contact_added');
        return response.data;
    } catch {
        throw new Error(`It was not possible to create a new contact with the phone number ${phoneNumber}.`);
    }
};
</script>

<style scoped>
@reference "@css/style.css";

.contact-phone-number-selectable :deep(.vx-selectable .vs__dropdown-option) {
    @apply p-0;
}
</style>
